import React, { useState, useEffect } from 'react';
import { MapPin, Navigation, Clock } from 'lucide-react';
import { Location, UserLocation } from '../types';
import { getCachedRoute } from '../utils/routes';
import { getImageUrl } from '../utils/images';
import { Link } from 'react-router-dom';

interface LocationCardProps {
  location: Location;
  userLocation?: UserLocation | null;
  onSelect: (location: Location) => void;
  isLoading?: boolean;
}

export const LocationCard: React.FC<LocationCardProps> = ({ location, userLocation, onSelect, isLoading }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [distance, setDistance] = useState<number | null>(null);
  const [driveTime, setDriveTime] = useState<number | null>(null);

  useEffect(() => {
    if (userLocation && location.coordinates) {
      getCachedRoute(userLocation, location.coordinates)
        .then(({ distance, duration }) => {
          setDistance(distance);
          setDriveTime(duration);
        })
        .catch(console.error);
    }
  }, [userLocation, location.coordinates]);

  useEffect(() => {
    if (isHovered || isTouched) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => 
          prev === (location.images?.length ?? 1) - 1 ? 0 : prev + 1
        );
      }, 2000);
      return () => clearInterval(interval);
    }
    return () => setCurrentImageIndex(0);
  }, [isHovered, isTouched, location.images?.length]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-3xl shadow-sm overflow-hidden h-[360px] animate-pulse">
        <div className="h-40 bg-gray-200" />
        <div className="p-4 space-y-4">
          <div className="h-6 bg-gray-200 rounded w-3/4" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
          <div className="h-16 bg-gray-200 rounded" />
          <div className="flex gap-2">
            <div className="h-6 bg-gray-200 rounded w-20" />
            <div className="h-6 bg-gray-200 rounded w-20" />
            <div className="h-6 bg-gray-200 rounded w-20" />
          </div>
        </div>
      </div>
    );
  }

  const formatDuration = (minutes: number) => {
    if (minutes > 60) {
      const hours = minutes / 60;
      const roundedHours = Math.round(hours * 10) / 10;
      return `${Number.isInteger(roundedHours) ? Math.floor(roundedHours) : roundedHours} г.`;
    }
    return `${minutes} хв`;
  };

  const images = location.images ?? [];
  const tags = location.tags ?? [];

  return (
    <Link 
      to={`/l/${location.short_id}`}
      className="bg-white rounded-3xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden h-[360px] flex flex-col"
      onClick={(e) => {
        e.preventDefault();
        onSelect(location);
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={() => setIsTouched(true)}
      onTouchEnd={() => setIsTouched(false)}
    >
      <div className="relative h-40 flex-shrink-0">
        {images.length > 0 ? (
          <img 
            src={getImageUrl(images[currentImageIndex])} 
            alt={location.name}
            className="w-full h-full object-cover transition-opacity duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
            <p className="text-gray-500">Няма фотаздымкаў</p>
          </div>
        )}
        {images.length > 0 && (
          <div className="absolute top-2 right-2 bg-black/50 px-3 py-1.5 rounded-full text-white text-xs">
            {currentImageIndex + 1}/{images.length}
          </div>
        )}
      </div>

      <div className="p-4 flex flex-col flex-1 min-h-0">
        <div className="mb-2">
          <h3 className="font-semibold text-gray-900 text-lg mb-1 line-clamp-1">{location.name}</h3>
          {location.coordinates && (
            <div className="flex items-center text-primary text-xs">
              <MapPin size={12} className="mr-1 flex-shrink-0" />
              <span className="truncate">
                {location.coordinates.lat.toFixed(4)}, {location.coordinates.lng.toFixed(4)}
              </span>
            </div>
          )}
        </div>

        {distance !== null && driveTime !== null && (
          <div className="flex items-center gap-3 text-sm text-gray-600 mb-3">
            <div className="flex items-center gap-1.5">
              <Navigation size={16} className="flex-shrink-0" />
              <span>{distance} км</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock size={16} className="flex-shrink-0" />
              <span>{formatDuration(driveTime)}</span>
            </div>
          </div>
        )}
        
        <p className="text-sm text-gray-600 mb-3 line-clamp-2 leading-relaxed">{location.shortDescription}</p>
        
        <div className="mt-auto">
          <div className="flex flex-wrap gap-1.5">
            {tags.slice(0, 3).map((tag) => (
              <span 
                key={tag}
                className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 text-xs text-gray-600"
              >
                #{tag}
              </span>
            ))}
            {tags.length > 3 && (
              <span className="text-xs text-gray-500 ml-1">+{tags.length - 3}</span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};